import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  getListOrganizationWebhooksQueryKey,
  useCreateOrganizationWebhook,
} from '@sara/api-client';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import {
  CreateWebhookForm,
  type CreateWebhookFormValues,
} from '../../components/CreateWebhookForm';
import { WebhookSecretReveal } from '../../components/WebhookSecretReveal';

interface CreateWebhookDialogProps extends DialogBaseProps {
  orgId: string;
}

function CreateWebhookDialog({ orgId, onClose }: CreateWebhookDialogProps) {
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);

  const { mutate, isPending, error } = useCreateOrganizationWebhook({
    mutation: {
      onSuccess: (data) => {
        void queryClient.invalidateQueries({
          queryKey: getListOrganizationWebhooksQueryKey({ orgId }),
        });
        setSecret(data.secret);
        toast.success('Webhook created', {
          description: "Copy the signing secret now — it won't be shown again.",
        });
      },
    },
  });

  function handleSubmit(values: CreateWebhookFormValues) {
    mutate({
      data: {
        url: values.url,
        label: values.label.trim() === '' ? undefined : values.label,
      },
      params: { orgId },
    });
  }

  if (secret) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            Webhook created
          </DialogTitle>
          <DialogDescription>
            Copy your new signing secret below. It won&rsquo;t be shown again.
          </DialogDescription>
        </DialogHeader>
        <WebhookSecretReveal secret={secret} onDone={onClose} />
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium tracking-tight">
          Add webhook receiver
        </DialogTitle>
        <DialogDescription>
          Signed event deliveries will be POSTed to this URL when sessions complete.
        </DialogDescription>
      </DialogHeader>
      <CreateWebhookForm
        onSubmit={handleSubmit}
        onCancel={onClose}
        isPending={isPending}
        error={error instanceof Error ? error.message : error ? 'Failed to create webhook' : null}
      />
    </DialogContent>
  );
}

export { CreateWebhookDialog };
